import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useContentReviewerNudge } from '../content-reviewer/useContentReviewerNudge.tsx';
import { useContentReviewerNudgeOnCriteriaBaseEligibility } from '../content-reviewer/useContentReviewerNudgeOnCriteriaBaseEligibility.ts';
import { useMeetingNotesNudge } from '../meeting-notes/useMeetingNotesNudge.tsx';
import { useMeetingNotesNudgeOnPublishSignal } from '../meeting-notes/useMeetingNotesNudgeOnPublishSignal.ts';
import { useStatusUpdatesAutomationNudgeAction } from '../status-updates/useStatusUpdatesAutomationNudgeAction.tsx';

import { useConfluenceAutomationsBaseEligibility } from './useConfluenceAutomationsBaseEligibility.ts';
import { useStudioPermissions } from './useStudioPermissions.ts';

import type {
	useConfluenceAutomationNudgeActionsQuery$data,
	useConfluenceAutomationNudgeActionsQuery$variables,
} from './__generated__/useConfluenceAutomationNudgeActionsQuery.graphql.ts';

export interface AutomationNudgeActionEntry {
	actionId: string;
	action: ConversationAction;
}

/** The settled `singleContent` slice of the nudge-actions query, passed to the content sub-hooks. */
export type AutomationNudgeContent = NonNullable<
	useConfluenceAutomationNudgeActionsQuery$data['singleContent']
>;

/** The settled `user` slice of the nudge-actions query, passed to the status-updates sub-hook. */
export type AutomationNudgeUser = NonNullable<
	useConfluenceAutomationNudgeActionsQuery$data['user']
>;

/**
 * Runs the nudge-actions query and returns ONLY settled, content-matched entities — never the raw
 * `useQueryInterop` result.
 *
 * IMPORTANT: DO NOT USE FRAGMENT SPREADS HERE
 *
 * Every field the sub-hooks need is selected DIRECTLY on this query (no colocated fragments), so the
 * reactive `useQueryInterop` data can be passed straight down as plain props. That keeps the whole
 * subtree suspense-free — there are no fragment reads left to suspend on a torn-down record — while
 * staying fully reactive.
 */
const useConfluenceAutomationNudgeActionsQuery = ({
	contentId,
	skipStatusUpdatesAutomation,
	skipContentReviewerNudge,
	skipUserEmails,
	isDraft,
	skip,
}: {
	contentId: string;
	skipStatusUpdatesAutomation: boolean;
	skipContentReviewerNudge: boolean;
	skipUserEmails: boolean;
	isDraft: boolean;
	skip: boolean;
}): {
	singleContent: AutomationNudgeContent | undefined;
	user: AutomationNudgeUser | undefined;
} => {
	const { data, loading } = useQueryInterop<
		useConfluenceAutomationNudgeActionsQuery$data,
		useConfluenceAutomationNudgeActionsQuery$variables
	>(
		graphql`
			query useConfluenceAutomationNudgeActionsQuery(
				$contentId: ID!
				$skipStatusUpdatesAutomation: Boolean!
				$skipContentReviewerNudge: Boolean!
				$skipUserEmails: Boolean!
				$isDraft: Boolean!
			) {
				user: confluence_atlassianUser(current: true) {
					__typename
					id
					emails @skip(if: $skipUserEmails) {
						value
						isPrimary
					}
				}
				singleContent(id: $contentId) {
					__typename
					id
					contentState(isDraft: $isDraft) @skip(if: $skipContentReviewerNudge) {
						id
						name
					}
					title @skip(if: $skipStatusUpdatesAutomation)
					space @skip(if: $skipStatusUpdatesAutomation) {
						id
					}
					ancestors @skip(if: $skipStatusUpdatesAutomation) {
						id
						title
					}
					history @skip(if: $skipStatusUpdatesAutomation) {
						ownedBy {
							... on KnownUser {
								accountId
							}
						}
					}
					metadata @skip(if: $skipStatusUpdatesAutomation) {
						sourceTemplateEntityId
					}
					children(first: 25) @skip(if: $skipStatusUpdatesAutomation) {
						nodes {
							id
							title
							metadata {
								createdDate
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				skipStatusUpdatesAutomation,
				skipContentReviewerNudge,
				skipUserEmails,
				isDraft,
			},
			skip,
		},
	);

	if (loading || !data) {
		return {
			singleContent: undefined,
			user: undefined,
		};
	}

	const settledContent =
		data.singleContent && data.singleContent.id === contentId ? data.singleContent : undefined;

	return {
		singleContent: settledContent,
		user: data.user ?? undefined,
	};
};

export const useConfluenceAutomationNudgeActions = (
	contentId: string,
): AutomationNudgeActionEntry[] => {
	const hasBaseEligibility = useConfluenceAutomationsBaseEligibility();
	const pageMode = useGetPageMode();

	const hasUserSeenStatusUpdatesAutomationNudge = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_STATUS_UPDATES,
	);
	const includeStatusUpdatesAutomation =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		!hasUserSeenStatusUpdatesAutomationNudge && fg('cc_automations_discovery_nudges');

	const includeContentReviewerNudge = useContentReviewerNudgeOnCriteriaBaseEligibility();
	const includeMeetingNotesNudge = useMeetingNotesNudgeOnPublishSignal(contentId);
	const isAnyNudgeEligible =
		includeStatusUpdatesAutomation || includeContentReviewerNudge || includeMeetingNotesNudge;
	const isDraft = pageMode === PageMode.EDIT;

	const { singleContent, user } = useConfluenceAutomationNudgeActionsQuery({
		contentId,
		skipStatusUpdatesAutomation: !includeStatusUpdatesAutomation,
		skipContentReviewerNudge: !includeContentReviewerNudge,
		skipUserEmails: !includeStatusUpdatesAutomation && !includeMeetingNotesNudge,
		isDraft,
		skip:
			!hasBaseEligibility ||
			!contentId ||
			!isAnyNudgeEligible ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			!fg('automations-integration-to-rovo-button-nudges'),
	});

	const contentReviewerNudgeAction = useContentReviewerNudge({
		content: singleContent,
		contentId,
	});

	const statusUpdatesAutomationNudgeAction = useStatusUpdatesAutomationNudgeAction({
		content: singleContent,
		user,
	});

	const meetingNotesNudgeAction = useMeetingNotesNudge({
		user,
		contentId,
	});

	const matchedActions = useMemo<AutomationNudgeActionEntry[]>(
		() =>
			[
				{ actionId: 'status-updates-automation', action: statusUpdatesAutomationNudgeAction },
				{ actionId: 'content-reviewer-nudge', action: contentReviewerNudgeAction },
				{ actionId: 'meeting-notes-nudge', action: meetingNotesNudgeAction },
			].filter((entry): entry is AutomationNudgeActionEntry => entry.action != null),
		[statusUpdatesAutomationNudgeAction, contentReviewerNudgeAction, meetingNotesNudgeAction],
	);

	// Defer the Studio permission check until at least one nudge has matched, so
	// the permission query never runs for the common case where nothing matches —
	// while still only running it once for all nudges.
	const { canCreateStudioAutomation } = useStudioPermissions({ skip: matchedActions.length === 0 });

	return useMemo<AutomationNudgeActionEntry[]>(
		() => (canCreateStudioAutomation && hasBaseEligibility ? matchedActions : []),
		[matchedActions, canCreateStudioAutomation, hasBaseEligibility],
	);
};
